import React, { useRef, useState } from 'react';
import { useDispatch } from "react-redux";
import { toggleErrorDialog } from 'actions';
import { Translate, I18n } from 'react-redux-i18n';
import PropTypes from 'prop-types';
import AuthFeature from 'components/AuthFeature';
import Modal from 'components/Modal';
import { AUTH_EDIT_TICKET } from 'constants/permission';
import { MAX_IMAGE_FILE_COUNT, MAX_VIDEO_FILE_COUNT } from 'constants/ticket';
import Button from 'components/Form/Button';
import Input, { TYPE_TEXTAREA } from 'components/Form/Input';
import FlexGroup from 'components/FlexGroup';
import BatchUploader from 'components/BatchUploader';
import useFileUpload from 'hooks/useFileUpload';
import ThumbnailItem from 'components/BatchUploader/ThumbnailItem';


const ACCEPTED_MEDIA = [
    // image
    'png', 'jpg', 'jpeg',
    // video
    'mp4', 'mov'
].map(ext => `.${ext}`).join(',');

const Comment = ({ notes_editable, updating, onUpdateComment }) => {
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const [text, setText] = useState('');
    const elComment = useRef(null);

    const {
        files,
        uploading,
        selecting,
        imageFiles,
        videoFiles,
        totalProgress,
        handleFileSelect,
        handleBatchUpload,
        removeFile,
        clearAllFiles,
    } = useFileUpload({
        maxImages: MAX_IMAGE_FILE_COUNT,
        maxVideos: MAX_VIDEO_FILE_COUNT
    });

    const fileUploadDisabled = !notes_editable || updating || selecting;
    const allFilesReady = files.length > 0 && files.every(file => file.thumbnailUrl);

    const handleSubmitComment = async () => {
        setShow(true);
        try {
            const attachments = await handleBatchUpload();
            const { type } = await onUpdateComment({
                notes: elComment.current.value,
                attachments
            });
            if (type === 'ERROR') {
                return dispatch(toggleErrorDialog(I18n.t('general_error_message')));
            }
            await clearAllFiles();
            setText('');
            elComment.current.value = '';
        } catch (error) {
            dispatch(toggleErrorDialog(
                I18n.t('general_error_message'),
            ));
        } finally {
            setShow(false);
        }
    }
    return (
        <>
            <section className="new-comment">
                <div className="comment-container">
                    <Input
                        type={ TYPE_TEXTAREA }
                        onChange={ (e) => setText(e.target.value) }
                        value={ elComment.current && elComment.current.value }
                        innerRef={ elComment }
                        name="notes"
                        withCaption={ false }
                        disabled={ fileUploadDisabled }
                    />
                </div>
                <FlexGroup end alignStart>
                    <FlexGroup
                        row
                        end
                        alignCenter
                        className="file-upload-wrapper">
                        <AuthFeature requiredList={ [AUTH_EDIT_TICKET] }>
                            <BatchUploader
                                fileUploadDisabled={ fileUploadDisabled }
                                isLoading={ updating || selecting }
                                files={files}
                                uploading={uploading}
                                imageFiles={imageFiles}
                                videoFiles={videoFiles}
                                handleFileSelect={handleFileSelect}
                                removeFile={removeFile}
                                acceptedTypes={ACCEPTED_MEDIA}
                            >
                                <Button
                                    type="button"
                                    color="primary"
                                    disabled={ fileUploadDisabled
                                        || (text === '' && !allFilesReady)
                                    }
                                    onClick={ handleSubmitComment }
                                >
                                    <Translate value="submit" />
                                </Button>
                            </BatchUploader>
                        </AuthFeature>
                    </FlexGroup>
                </FlexGroup>
            </section>
            { show && (
                <Modal
                    title={I18n.t('uploading')}
                    className="upload-modal"
                    disableCloseBtn
                >
                    <div className="file-flex">
                        {files.map(file => (
                            <ThumbnailItem
                                key={file.id}
                                file={file}
                                uploading={ uploading }
                                showProgress={ true }
                                specifiedSize
                            />
                        ))}
                    </div>
                    <span className="file-progress-display">{`${I18n.t('completed')}：${totalProgress}%`}</span>
                </Modal>
            ) }
        </>
    )
}

Comment.propTypes = {
    notes_editable: PropTypes.bool,
    updating: PropTypes.bool,
    onUpdateComment: PropTypes.func
};

Comment.defaultProps = {
    notes_editable: false,
    updating: false,
    onUpdateComment: () => {}
};

export default Comment;
